import React from 'react';
import { XIcon } from '../canvas/icons';
import { safePad } from '../../../utils/safeArea';

/**
 * A bottom-docked sheet on the composer's canvas (handoff §8): #0B0B0B, a grab handle, a
 * one-line title with an optional dim suffix, a round ✕ unless locked, and a grid body so a
 * tall sheet scrolls instead of squeezing its rows. A tap on the canvas behind it closes it —
 * unless it is locked, in which case nothing does.
 */
export const FILL = 'rgba(255,255,255,.12)';
export const FILL_ROW = 'rgba(255,255,255,.07)';

/** A 999-radius pill: white when on, a white 12% fill when off. */
export const pill = (on) => ({ background: on ? '#fff' : FILL, color: on ? '#000' : '#fff' });

export const PILL_BASE = { border: 0, borderRadius: 999, fontFamily: 'inherit', fontWeight: 700, cursor: 'pointer', transition: 'background-color .2s, color .2s' };

/** The 44px white CTA the sheets end on. */
export function SheetCta({ children, onClick, disabled = false, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{ ...PILL_BASE, marginTop: 4, height: 44, background: '#fff', color: '#000', fontSize: 12, letterSpacing: '0.14em', opacity: disabled ? 0.4 : 1 }}
      {...rest}
    >
      {children}
    </button>
  );
}

export default function Sheet({ title, suffix = '', locked = false, onClose, children, testId = 'sheet', zIndex = 10 }) {
  return (
    <>
      <div
        aria-hidden
        data-testid="sheet-backdrop"
        onPointerDown={locked ? undefined : onClose}
        style={{ position: 'absolute', inset: 0, zIndex: zIndex - 1 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        className="font-mono text-white"
        style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex, background: '#0B0B0B', padding: safePad('10px 16px 20px'), display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gridAutoRows: 'max-content', gap: 12, maxHeight: '78%', overflowY: 'auto', scrollbarWidth: 'none', boxSizing: 'border-box', animation: 'prcSlideUp .44s cubic-bezier(.2,.7,.2,1) both' }}
      >
        <span aria-hidden style={{ justifySelf: 'center', width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,.3)' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em' }}>
            {title} {suffix && <span style={{ color: 'rgba(255,255,255,.55)' }}>{suffix}</span>}
          </span>
          {!locked && (
            <button type="button" onClick={onClose} aria-label="Close" className="prc-pill" style={{ ...PILL_BASE, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, background: 'rgba(255,255,255,.14)', color: '#fff', borderRadius: '50%' }}>
              <XIcon />
            </button>
          )}
        </div>
        {children}
      </div>
    </>
  );
}
